import { useMutation, useQueryClient } from "@tanstack/react-query";
import { SlidersHorizontal } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePageActions } from "@/app/pageActions/PageActionsContext";
import { isApiClientError } from "@/shared/api/errors";
import { useAuth } from "@/shared/auth/AuthContext";
import { hasPermission, PermissionCodes } from "@/shared/auth/permissions";
import {
  Banner,
  ConfirmationDialog,
  DataTable,
  DetailField,
  DetailFieldGrid,
  EntityFormDialog,
  EntityPeekModal,
  FormField,
  Input,
  Select,
  TextArea,
  useToast,
  type DataTableRowAction,
} from "@/shared/ui";
import { entityFormModalSizeFor } from "@/shared/ui/core/entityFormModalPolicy";
import { formatInteger, formatNumber, parseNumber } from "@/shared/lib/format";
import {
  ListPageTemplate,
  SearchStatusFilterBar,
} from "@/shared/ui/patterns/ListPageTemplate";
import {
  createAppSetting,
  deleteAppSetting,
  updateAppSetting,
  type AppSetting,
  type AppSettingInput,
} from "@/modules/sys/api/sysApi";
import { useAppSettingsListQuery } from "@/modules/sys/queries/useSysQueries";

const settingsQueryKey = ["sys", "settings"] as const;

const VALUE_TYPES = [
  { value: "text", label: "Chữ" },
  { value: "int", label: "Số nguyên" },
  { value: "decimal", label: "Số thập phân" },
  { value: "bool", label: "Có / Không" },
] as const;

const VALUE_TYPE_FILTER_OPTIONS = [
  { value: "all", label: "Tất cả" },
  ...VALUE_TYPES.map((item) => ({ value: item.value, label: item.label })),
];

type ValueType = (typeof VALUE_TYPES)[number]["value"];

function valueTypeLabel(valueType: string): string {
  return VALUE_TYPES.find((item) => item.value === valueType)?.label ?? valueType;
}

function displayValue(setting: Pick<AppSetting, "valueType" | "value">): string {
  if (setting.valueType === "bool") return setting.value === "true" ? "Có" : "Không";
  if (setting.valueType === "int") return formatInteger(setting.value, setting.value);
  if (setting.valueType === "decimal") {
    return formatNumber(setting.value, { maxFractionDigits: 8, fallback: setting.value });
  }
  return setting.value;
}

function toInputValue(setting?: AppSetting): string {
  if (!setting) return "";
  if (setting.valueType === "int" || setting.valueType === "decimal") return displayValue(setting);
  return setting.value;
}

function toStoredNumber(valueType: string, raw: string): string {
  const trimmed = raw.trim();
  if (valueType !== "int" && valueType !== "decimal") return trimmed;
  const parsed = parseNumber(trimmed);
  if (parsed === null) return trimmed;
  if (valueType === "int") return String(Math.trunc(parsed));
  return String(parsed);
}

function apiErrorMessage(error: unknown, fallback: string): string {
  return isApiClientError(error) ? error.message : fallback;
}

/** `SCR-SYS-DEFAULTS` — giá trị mặc định mở theo khóa. */
export function DefaultsAdminPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = hasPermission(
    user?.permissions ?? [],
    PermissionCodes.SysSettingManage,
  );
  const list = useAppSettingsListQuery();
  const [viewing, setViewing] = useState<AppSetting | null>(null);
  const [editing, setEditing] = useState<AppSetting | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<AppSetting | null>(null);

  usePageActions(
    useMemo(
      () => [
        {
          id: "settings-hub",
          label: "Thiết lập hệ thống",
          isQuickLink: true,
          onClick: () => navigate("/ops/sys/settings"),
        },
        ...(canManage
          ? [
              {
                id: "create",
                label: "Thêm giá trị",
                variant: "primary" as const,
                onClick: () => setCreating(true),
              },
            ]
          : []),
      ],
      [canManage, navigate],
    ),
  );

  return (
    <>
      <ListPageTemplate
        loading={list.isLoading}
        errorTitle={list.isError ? "Không tải được giá trị mặc định" : undefined}
        errorDescription={
          list.isError && isApiClientError(list.error) ? list.error.message : undefined
        }
        onRetry={() => void list.refetch()}
        filtersOpen={list.filtersOpen}
        filterActiveCount={list.activeFilterCount}
        filter={
          <SearchStatusFilterBar
            open={list.filtersOpen}
            q={list.q}
            onQChange={list.setQ}
            searchPlaceholder="Tìm theo tên / khóa / giá trị"
            status={list.status}
            onStatusChange={list.setStatus}
            statusOptions={VALUE_TYPE_FILTER_OPTIONS}
            statusLabel="Kiểu giá trị"
            onClearAll={list.clearFilters}
          />
        }
        pagination={{
          page: list.page,
          pageSize: list.pageSize,
          total: list.total,
          loading: list.isFetching,
          onPageChange: list.setPage,
          onPageSizeChange: list.setPageSize,
        }}
      >
        <DataTable<AppSetting>
          getRowId={(row) => row.id}
          rows={list.items}
          rowNumberStart={list.rowNumberStart}
          pageLoading={list.isFetching && !list.isLoading}
          loadingText="Đang tải trang..."
          emptyKeepHeader
          emptyTitle={
            list.q || (list.status && list.status !== "all")
              ? "Không tìm thấy giá trị phù hợp"
              : "Chưa có giá trị mặc định"
          }
          emptyDescription={
            list.q || (list.status && list.status !== "all")
              ? "Thử đổi từ khóa hoặc xóa bộ lọc."
              : undefined
          }
          emptyIcon={<SlidersHorizontal className="size-6" aria-hidden />}
          minWidthClassName="min-w-[860px]"
          onRowClick={setViewing}
          getRowActions={(row): DataTableRowAction[] =>
            canManage
              ? [
                  { id: "edit", label: "Sửa", onClick: () => setEditing(row) },
                  { id: "delete", label: "Xóa", danger: true, onClick: () => setDeleting(row) },
                ]
              : []
          }
          columns={[
            {
              id: "label",
              header: "Tên",
              cell: (row) => row.label,
            },
            {
              id: "key",
              header: "Khóa",
              mono: true,
              cell: (row) => row.key,
            },
            {
              id: "type",
              header: "Kiểu",
              cell: (row) => valueTypeLabel(row.valueType),
            },
            {
              id: "value",
              header: "Giá trị",
              mono: true,
              cell: (row) => displayValue(row),
            },
          ]}
        />
      </ListPageTemplate>

      <EntityPeekModal
        open={viewing != null}
        onOpenChange={(open) => {
          if (!open) setViewing(null);
        }}
        title={viewing?.label ?? "Giá trị mặc định"}
        description={viewing?.key}
        icon={<SlidersHorizontal className="size-4" aria-hidden />}
        iconTone="info"
        headerTone="info"
        size="sm"
        height="default"
        footerSize="sm"
      >
        {viewing ? (
          <DetailFieldGrid columns={2}>
            <DetailField label="Tên">{viewing.label}</DetailField>
            <DetailField label="Khóa" mono>
              {viewing.key}
            </DetailField>
            <DetailField label="Kiểu">{valueTypeLabel(viewing.valueType)}</DetailField>
            <DetailField label="Giá trị" mono>
              {displayValue(viewing)}
            </DetailField>
            <DetailField label="Mô tả">{viewing.description || "—"}</DetailField>
          </DetailFieldGrid>
        ) : null}
      </EntityPeekModal>

      {creating ? (
        <SettingFormDialog mode="create" onOpenChange={setCreating} />
      ) : null}
      {editing ? (
        <SettingFormDialog
          mode="edit"
          setting={editing}
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
        />
      ) : null}
      {deleting ? (
        <DeleteSettingDialog
          setting={deleting}
          onOpenChange={(open) => {
            if (!open) setDeleting(null);
          }}
        />
      ) : null}
    </>
  );
}

function SettingFormDialog({
  mode,
  setting,
  onOpenChange,
}: {
  mode: "create" | "edit";
  setting?: AppSetting;
  onOpenChange: (open: boolean) => void;
}) {
  const { http } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [key, setKey] = useState(setting?.key ?? "");
  const [label, setLabel] = useState(setting?.label ?? "");
  const [description, setDescription] = useState(setting?.description ?? "");
  const [valueType, setValueType] = useState<ValueType>(
    (setting?.valueType as ValueType) || "text",
  );
  const [value, setValue] = useState(toInputValue(setting));
  const [formError, setFormError] = useState<string | null>(null);
  const dirty =
    mode === "create"
      ? key.trim() !== "" ||
        label.trim() !== "" ||
        description.trim() !== "" ||
        value.trim() !== "" ||
        valueType !== "text"
      : label !== (setting?.label ?? "") ||
        (description.trim() || null) !== (setting?.description ?? null) ||
        valueType !== setting?.valueType ||
        value !== toInputValue(setting);

  const saveMut = useMutation({
    mutationFn: (body: AppSettingInput) =>
      mode === "create"
        ? createAppSetting(http, body)
        : updateAppSetting(http, setting!.id, body),
    onSuccess: async () => {
      toast({
        tone: "success",
        title: "Thao tác thành công",
        message: mode === "create" ? "Đã thêm giá trị mặc định." : "Đã lưu giá trị mặc định.",
      });
      onOpenChange(false);
      await qc.invalidateQueries({ queryKey: settingsQueryKey });
    },
    onError: (error) => {
      setFormError(apiErrorMessage(error, "Không lưu được giá trị mặc định"));
    },
  });

  const save = () => {
    setFormError(null);
    const body: AppSettingInput = {
      label: label.trim(),
      description: description.trim() || null,
      valueType,
      value: toStoredNumber(valueType, value),
    };
    if (mode === "create") body.key = key.trim();
    saveMut.mutate(body);
  };

  return (
    <EntityFormDialog
      open
      onOpenChange={onOpenChange}
      mode={mode}
      title={mode === "create" ? "Thêm giá trị mặc định" : `Sửa ${setting?.label ?? ""}`}
      size={entityFormModalSizeFor("compact")}
      dirty={dirty}
      saving={saveMut.isPending}
      loadingLabel="Đang lưu..."
      primaryLabel="Lưu"
      cancelLabel="Hủy"
      onPrimary={save}
    >
      <div className="space-y-3 py-1">
        {formError ? <Banner tone="danger" title={formError} /> : null}
        <FormField label="Khóa" required>
          <Input
            value={key}
            readOnly={mode === "edit"}
            autoComplete="off"
            className={mode === "edit" ? "bg-readonly-bg font-mono text-readonly-fg" : "font-mono"}
            placeholder="pur.vatRate"
            onChange={(e) => setKey(e.target.value)}
          />
        </FormField>
        <FormField label="Tên" required>
          <Input value={label} autoComplete="off" onChange={(e) => setLabel(e.target.value)} />
        </FormField>
        <Select
          label="Kiểu"
          value={valueType}
          options={VALUE_TYPES.map((item) => ({ value: item.value, label: item.label }))}
          onValueChange={(next) => {
            const typed = next as ValueType;
            setValueType(typed);
            if (typed === "bool" && value !== "true" && value !== "false") setValue("true");
          }}
        />
        {valueType === "bool" ? (
          <Select
            label="Giá trị"
            value={value === "false" ? "false" : "true"}
            options={[
              { value: "true", label: "Có" },
              { value: "false", label: "Không" },
            ]}
            onValueChange={setValue}
          />
        ) : (
          <FormField
            label="Giá trị"
            required={valueType !== "text"}
            hint={valueType === "decimal" ? "0,08 = 8%." : undefined}
          >
            <Input
              value={value}
              autoComplete="off"
              className="font-mono"
              onChange={(e) => setValue(e.target.value)}
            />
          </FormField>
        )}
        <FormField label="Mô tả">
          <TextArea
            value={description}
            rows={3}
            onChange={(e) => setDescription(e.target.value)}
          />
        </FormField>
      </div>
    </EntityFormDialog>
  );
}

function DeleteSettingDialog({
  setting,
  onOpenChange,
}: {
  setting: AppSetting;
  onOpenChange: (open: boolean) => void;
}) {
  const { http } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const deleteMut = useMutation({
    mutationFn: () => deleteAppSetting(http, setting.id),
    onSuccess: async () => {
      toast({ tone: "success", title: "Thao tác thành công", message: "Đã xóa giá trị mặc định." });
      onOpenChange(false);
      await qc.invalidateQueries({ queryKey: settingsQueryKey });
    },
    onError: (error) => {
      toast({
        tone: "error",
        title: "Không xóa được",
        message: apiErrorMessage(error, "Không xóa được giá trị mặc định"),
      });
    },
  });

  return (
    <ConfirmationDialog
      open
      onOpenChange={onOpenChange}
      title="Xóa giá trị mặc định"
      description={`${setting.label} (${setting.key}) sẽ không còn được dùng làm mặc định.`}
      tone="destructive"
      confirmLabel="Xóa"
      loading={deleteMut.isPending}
      loadingLabel="Đang xóa..."
      onConfirm={() => deleteMut.mutate()}
    />
  );
}
